import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, Check } from "lucide-react";
import Header from "../components/Header";
import { api, formatApiErrorDetail } from "../lib/api";
import { useTheme } from "../context/ThemeContext";

export default function ForgotPassword() {
  const { theme } = useTheme();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim()) return;
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#030303] text-white relative grain">
      <Header />
      <div className="relative min-h-screen flex items-center justify-center px-6 pt-24 pb-16">
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[400px] w-[400px] rounded-full blur-3xl opacity-20 pointer-events-none"
          style={{ backgroundColor: theme.color }}
        />
        <div
          className="relative w-full max-w-md p-8 bg-[#0A0A0A] border"
          style={{ borderColor: theme.color, boxShadow: `0 0 50px ${theme.color}33` }}
          data-testid="forgot-password-card"
        >
          {!sent ? (
            <>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">
                <Mail className="h-3 w-3 theme-text" /> Reset password
              </div>
              <h1 className="font-display font-black text-3xl tracking-tight">Forgot password?</h1>
              <p className="text-sm text-zinc-400 mt-1">
                Enter your email and we&apos;ll send you a secure link to reset it.
              </p>
              <form onSubmit={onSubmit} className="mt-8 space-y-4">
                <label className="block">
                  <span className="block text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1.5">Email</span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@domain.com"
                    autoComplete="email"
                    data-testid="forgot-email"
                    className="w-full h-11 px-4 bg-black/60 border border-white/10 focus:border-[var(--theme-primary)] outline-none text-sm"
                  />
                </label>
                {error && <p className="text-sm text-red-400" data-testid="forgot-error">{error}</p>}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 h-12 font-display font-bold tracking-wide disabled:opacity-60"
                  style={{ backgroundColor: theme.color, color: "#000", boxShadow: `0 0 28px ${theme.color}77` }}
                  data-testid="forgot-submit"
                >
                  {loading ? "SENDING…" : "SEND RESET LINK"}
                </button>
                <Link
                  to="/login"
                  className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-white transition-colors pt-2"
                >
                  <ArrowLeft className="h-3 w-3" /> Back to login
                </Link>
              </form>
            </>
          ) : (
            <div className="text-center py-4 space-y-5" data-testid="forgot-success">
              <div
                className="mx-auto h-14 w-14 rounded-full flex items-center justify-center pulse-glow"
                style={{ backgroundColor: theme.color }}
              >
                <Check className="h-7 w-7 text-black" strokeWidth={3} />
              </div>
              <h3 className="font-display font-black text-xl">Check your inbox</h3>
              <p className="text-sm text-zinc-400 leading-relaxed">
                If an account matches <span className="theme-text">{email}</span>, a reset link is on its way.
                It expires in 1 hour.
              </p>
              <Link
                to="/login"
                className="inline-flex items-center gap-1.5 text-xs theme-text hover:underline pt-2"
              >
                <ArrowLeft className="h-3 w-3" /> Back to login
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
